/**
 * Which solution-analysis layout this student sees, and remembering the one
 * they pick.
 *
 * Resolution order:
 *   1. their own choice from `localStorage` — read synchronously so the
 *      remembered tab is right on first paint, with no round trip and no flash
 *      of the wrong layout
 *   2. their choice as stored on the server, which is what makes it follow them
 *      to another device or survive cleared storage
 *   3. a balanced assignment derived from the user id
 *
 * A switch writes both copies. They can disagree only when the student switched
 * on another device, and the login write settles that as most-recent-login-wins
 * — enough for a tab preference, and not worth a reconciliation protocol.
 *
 * See docs/FeatureDesigns/solution-analysis-layouts/solution-analysis-layouts-target.md.
 */
import {
    resolveLayoutId, isOfferedLayoutId, shouldOfferLayoutFeedback,
    LAYOUT_FEEDBACK_SAMPLE_RATE, type SolutionAnalysisLayoutId,
} from '@padcanvas/shared';
import { saveLayoutChoice } from '../../api/layoutEvents';

const STORAGE_KEY = 'solution_analysis_layout';

/** Their explicit choice, or null. A retired or unrecognised value reads as no
 *  choice — `resolveLayoutId` re-assigns rather than stranding them on a layout
 *  nobody else can reach. */
export function getStoredLayout(): SolutionAnalysisLayoutId | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isOfferedLayoutId(stored) ? stored : null;
    } catch {
        return null;
    }
}

export function setStoredLayout(layoutId: SolutionAnalysisLayoutId): void {
    try {
        localStorage.setItem(STORAGE_KEY, layoutId);
    } catch {
        /* private window / storage disabled — the balanced assignment still
           applies, they just re-derive it next visit. */
    }
}

export function resolveLayoutForUser(userId: string, serverChoice?: string | null): SolutionAnalysisLayoutId {
    return resolveLayoutId(userId, getStoredLayout() ?? serverChoice);
}

/** Seed the device copy from the server's at login, so a student arriving on a
 *  new device lands on the layout they picked elsewhere. */
export function seedStoredLayout(serverChoice?: string | null): void {
    if (isOfferedLayoutId(serverChoice) && !getStoredLayout()) setStoredLayout(serverChoice);
}

/** Record a deliberate switch in both places: localStorage synchronously so the
 *  next render is instant, the server best-effort. */
export function rememberLayoutChoice(layoutId: SolutionAnalysisLayoutId): void {
    setStoredLayout(layoutId);
    saveLayoutChoice(layoutId);
}

// ── The sampled feedback prompt ──────────────────────────────────────

const ASKED_KEY = 'solution_analysis_layout_asked';
const FORCE_KEY = 'solution_analysis_layout_force_feedback';

function session(): Storage | null {
    try { return window.sessionStorage; } catch { return null; }
}

/**
 * `?layoutFeedback=1` forces the prompt on every analysis, for looking at it
 * without solving thirty questions. Latched into sessionStorage so it survives
 * navigation, and cleared by `?layoutFeedback=0`.
 *
 * Answers given while forced are flagged on the event, so the reader can leave
 * them out — otherwise a few minutes of testing would outweigh a day of real
 * responses in a small sample.
 */
export function isFeedbackForced(): boolean {
    const store = session();
    if (!store) return false;
    try {
        const param = new URLSearchParams(window.location.search).get('layoutFeedback');
        if (param === '1') store.setItem(FORCE_KEY, '1');
        if (param === '0') store.removeItem(FORCE_KEY);
        return store.getItem(FORCE_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Whether to offer the prompt for this analysis.
 *
 * Sampled at {@link LAYOUT_FEEDBACK_SAMPLE_RATE}% of analyses, deterministically
 * per (student, analysis) so it cannot flicker in and out as they navigate —
 * AND at most once per session. Without the session cap the rate compounds: at
 * 30% a twelve-question set asks 3.6 times and reads as nagging, which is how
 * you train students to dismiss it without reading.
 */
export function shouldAskForFeedback(userId: string, submissionId: string): boolean {
    if (isFeedbackForced()) return true;
    if (session()?.getItem(ASKED_KEY) === '1') return false;
    return shouldOfferLayoutFeedback(userId, submissionId, LAYOUT_FEEDBACK_SAMPLE_RATE);
}

/** One ask per session, whether or not they answered — a second ask after a
 *  dismissal is the nag the cap exists to prevent. */
export function markFeedbackAsked(): void {
    try { session()?.setItem(ASKED_KEY, '1'); } catch { /* no-op */ }
}
